import { useEffect, useMemo, useState } from "react"
import { IoClose, IoSearch } from "react-icons/io5"

import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import {
  fetchSearchResult,
  findSearchTypeAndId,
  getResultsWithSearchTerm,
  searchGroupsBy,
  SearchResultResponseType,
  SearchType,
} from "@fuse/sharedComponents/globalSearchContext/controller"
import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import { debounce, get, isEmpty, uniqBy } from "lodash-es"
import moment from "moment"
import { useRecoilValue } from "recoil"

import { allEventsSelectors } from "@fuse/recoil/events"
import { userState } from "@fuse/recoil/signup"

import { useSearchedEvent } from "@api/services/events/useEventsApi"

import { isSupportedAppUrl } from "@fuse/utils/common"

import EmptySearchImage from "@fuse/assets/images/3d-glassy-coupons.svg"

const GlobalSearchModal = ({
  hideSearchModal,
}: {
  hideSearchModal: () => void
}) => {
  const [searchQuery, setSearchQuery] = useState("")
  const localEventMap = useRecoilValue(allEventsSelectors)

  const user = useRecoilValue(userState)

  const [result, setResult] = useState<SearchResultResponseType[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const loggedInUserId = get(user, "uid", "")

  const onSearchByLink = async (content: string) => {
    const searchTypeAndId = findSearchTypeAndId(content)

    if (searchTypeAndId) {
      // Has valid url
      const localEvents = Object.values(localEventMap)?.map((event) => ({
        title: get(event, "eventName", ""),
        id: get(event, "eventId", ""),
        type: SearchType.Events,
        startTime: get(event, "timing.eventStartTime", ""),
        image: get(event, "flyerImage", ""),
        icon: "🗓️",
        subTitle: get(event, "eventDescription", ""),
        minTicketPrice: get(event, "minTicketPrice", null),
        isAdmin: get(event, "initiatorUid", "") === loggedInUserId,
      }))
      const searchResult = getResultsWithSearchTerm(
        searchTypeAndId.id,
        localEvents,
        searchGroupsBy.Id,
      )
      setResult(uniqBy(searchResult, "id"))
      if (searchResult.length === 0) {
        setIsLoading(true)
        fetchSearchResult(searchTypeAndId.id, loggedInUserId)
          .then((result) => {
            if (!isEmpty(result)) {
              setResult([result])
            }
          })
          .catch((err: { success: boolean }) => {
            if (!err?.success) setResult([])
          })
          .finally(() => {
            setIsLoading(false)
          })
      }
    } else {
      // Handle invalid or restriction bottom sheet
      // TODO: No result found UI is not ready yet.
    }
  }

  useEffect(() => {
    // Disable scrolling when the modal is open
    document.body.classList.add("overflow-hidden")

    // Cleanup when the component unmounts
    return () => {
      document.body.classList.remove("overflow-hidden")
    }
  }, [])

  const debouncedSearchByLink = useMemo(
    () =>
      debounce((text: string) => {
        onSearchByLink(text)
      }, 500),
    [],
  )

  const isSearchEnabled = useMemo(() => {
    return !!searchQuery && searchQuery.length > 0
  }, [searchQuery])

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("")
  const debouncedSearchByTermFn = useMemo(
    () =>
      debounce((text: string) => {
        setDebouncedSearchTerm(text)
      }, 500),
    [],
  )

  const queryBodyForSearchFilter = useMemo(() => {
    return {
      searchTerm: debouncedSearchTerm,
    }
  }, [debouncedSearchTerm])

  const {
    searchedResult,
    isLoading: fetchingSearchedResult,
    refetchByTerm: refetchSearch,
  } = useSearchedEvent(
    queryBodyForSearchFilter,
    isSearchEnabled,
    "TERM_BASED_SEARCH",
  )

  useEffect(() => {
    if (!fetchingSearchedResult) {
      const fetchedResult = [
        ...searchedResult.map((item) => {
          return {
            id: item._id,
            title: item._source.eventName,
            type: SearchType.Events,
            startTime: item._source.timing.eventStartTime,
            image: item._source.flyerImage,
            icon: "🗓️",
            subTitle: item._source.eventDescription,
            minTicketPrice: item._source.minTicketPrice || null,
            isAdmin: item._source.initiatorUid === loggedInUserId,
          }
        }),
      ]
      console.log("fetchedResult", { fetchedResult })
      setResult(uniqBy(fetchedResult, "id"))
    }
  }, [searchedResult, fetchingSearchedResult])

  useEffect(() => {
    if (debouncedSearchTerm) {
      refetchSearch()
    }
  }, [debouncedSearchTerm])

  const onTextChange = (inputText: string) => {
    setSearchQuery(inputText)
    if (isSupportedAppUrl(inputText)) {
      debouncedSearchByLink(inputText)
    } else {
      // const localEvents = Object.values(localEventMap)?.map((event) => ({
      //   title: get(event, "eventName", ""),
      //   id: get(event, "eventId", ""),
      //   type: SearchType.Events,
      //   startTime: get(event, "timing.eventStartTime", ""),
      //   image: get(event, "flyerImage", ""),
      //   icon: "🗓️",
      //   subTitle: get(event, "eventDescription", ""),
      //   minTicketPrice: get(event, "minTicketPrice", null),
      //   isAdmin: get(event, "initiatorUid", "") === loggedInUserId,
      // }))

      debouncedSearchByTermFn(inputText)
      // const result = getResultsWithSearchTerm(inputText, localEvents)

      // setResult(uniqBy(result, "id"))
    }
  }

  const handleOnEvent = (eventId: string) => {
    window.location.href = `/events/${eventId}`
  }

  return (
    <div className="fixed inset-0 flex justify-center bg-black bg-opacity-60 z-50">
      <div className="text-white rounded-lg shadow-lg w-full md:w-[90%] max-w-3xl p-6 pt-16">
        {(isLoading || fetchingSearchedResult) && <LoadingSpinner />}
        <div className="flex flex-row rounded-full px-6 items-center justify-center bg-white/30 backdrop-blur-sm border-t-[0.5px] border-gray-400">
          {/* <BlurWrapper className="flex flex-row rounded-full px-6 items-center justify-center"> */}
          <IoSearch className="text-white text-xl mr-4" />

          <input
            type="text"
            placeholder="Find an event"
            value={searchQuery}
            onChange={(e) => onTextChange(e.target.value)}
            className="w-full h-12 bg-transparent text-white focus:outline-none"
          />

          <div className="flex">
            <div
              className="flex h-6 w-6 rounded-full items-center justify-center text-center bg-white/20"
              onClick={() => hideSearchModal()}
            >
              <IoClose className="text-white" />
            </div>
          </div>
          {/* </BlurWrapper> */}
        </div>

        {/* Search Results */}
        <div className="mt-4 max-h-[80vh] overflow-y-auto">
          {result.length
            ? result?.map((result, index) => {
                const image = get(result, "image", null)
                const icon = get(result, "icon", "")
                const title = get(result, "title", "")
                const startTime = get(result, "startTime", "")
                const ticketMinPrice = get(result, "ticketMinPrice", null)
                const subTitle = get(result, "subTitle", "")
                const isAdmin = get(result, "isAdmin", false)
                const id = get(result, "id", "")

                return (
                  <BlurWrapper
                    key={index}
                    className="flex items-center space-x-4 transition rounded-lg p-6 mb-3"
                    onClick={() => handleOnEvent(id)}
                  >
                    {image ? (
                      <img
                        src={image}
                        alt={title}
                        className="w-20 min-h-20 h-full rounded-lg object-cover bg-white35"
                      />
                    ) : (
                      <div className="w-20 min-h-20 h-full rounded-lg bg-white35 flex text-center items-center justify-center">
                        <h3 className="text-lg font-semibold">{icon}</h3>
                      </div>
                    )}

                    <div className="flex flex-col items-start gap-0.5">
                      <h3 className="text-lg font-semibold text-start">
                        {title}
                      </h3>

                      <div className="flex flex-row gap-2">
                        <BlurWrapper
                          overlayOpacityDefault="5"
                          overlayColorDefault="dark"
                          className="p-1 px-2 items-center justify-center rounded-lg"
                        >
                          <p className="text-xs text-white text-center">
                            🗓️ {moment(startTime).format("ddd M/DD • hh:mm A")}
                          </p>
                        </BlurWrapper>

                        {ticketMinPrice && (
                          <BlurWrapper
                            overlayOpacityDefault="5"
                            overlayColorDefault="dark"
                            className="p-1 px-2 items-center justify-center rounded-lg"
                          >
                            <p className="text-xs text-white">
                              {ticketMinPrice}
                            </p>
                          </BlurWrapper>
                        )}
                      </div>

                      <p className="text-white opacity-50 text-start line-clamp-2 hidden md:block">
                        {`${subTitle?.substring(0, 100)}...`}
                        <span>{isAdmin && " • Hosting"}</span>
                      </p>
                      <p className="text-white opacity-50 text-start line-clamp-2 md:hidden">
                        {`${subTitle?.substring(0, 40)}...`}
                        <span>{isAdmin && " • Hosting"}</span>
                      </p>
                    </div>
                  </BlurWrapper>
                )
              })
            : null}

          {!isEmpty(debouncedSearchTerm) &&
            !result.length &&
            !(isLoading || fetchingSearchedResult) && (
              <BlurWrapper className="flex flex-col items-center justify-center space-x-4 transition rounded-lg p-6 mb-3 ">
                <div className="flex aspect-square bg-[#ffffff20] items-center justify-center  space-x-4 transition rounded-lg p-10 mb-3 ">
                  <img src={EmptySearchImage} className=" flex " />
                </div>
                <div className="w-full font-semibold ">No result is found</div>
                <div className="text-[#FFFFFF80]">
                  It seems like there are no events under that name
                </div>
              </BlurWrapper>
            )}
        </div>
      </div>
    </div>
  )
}

export default GlobalSearchModal
